import { useCallback, useMemo, useState } from 'react';

import { Box, List, ListItem, ListItemText, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ActionItemKeys, NotificationResponse, PatientFilledForm } from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import {
  trackConfirmedToDoDismissal, trackDismissToDo, useAllActionItems, useL10n
} from '@uplift/hooks';
import { UserRole } from '@uplift/types';
import { Button, StackableCard } from '@uplift/ui-v2';
import { mapAssessmentScoreToDescription } from '@uplift/utils';

import { LineItem, LineItemProps } from '../form-display';
import { NoReferModal } from './no-refer.modal';
import { PsychReferralModal } from './psych-referral.modal';

export type HighRiskProps = {
  date: string;
  formNameMap: Record<string, string>;
  patient: PatientFilledForm;
  time: string;
};

export const HighRiskAssessment: React.VFC<HighRiskProps> = ({ date, formNameMap, patient, time }) => {
  const { classes } = useStyles();
  const l10n = useL10n();
  const { dismiss } = useAllActionItems();
  const me = useCurrentuser();

  const [ modalOpen, setModalOpen ] = useState<null | 'refer' | 'defer'>(null);

  const phq9Desc = mapAssessmentScoreToDescription('phq9', l10n);
  const gad7Desc = mapAssessmentScoreToDescription('gad7', l10n);

  const descMap = useMemo(() => ({
    'PHQ-9': phq9Desc,
    'GAD-7': gad7Desc,
  }), [ gad7Desc, phq9Desc ]);

  const formData: LineItemProps[] = useMemo(
    () => Object.keys(patient.assessments).reduce((acc, cur) => {
      const formName = formNameMap[cur];
      const formData = patient.assessments[cur];

      return [
        ...acc,
        {
          label: formName,
          attention: formData.shouldNotify,
          score: formData.score,
          description: descMap[formName]?.(formData.score),
          completed: true
        }
      ];
    }, []),
    [ descMap, formNameMap, patient.assessments ]
  );

  const handleRefer = useCallback(() => {
    setModalOpen('refer');
  }, []);

  const handleDefer = useCallback(() => {
    setModalOpen('defer');
  }, []);

  const handleDismmiss = useCallback((data: Record<string, string>, response: NotificationResponse = NotificationResponse.dismiss) => {
    trackDismissToDo({
      toDoType: ActionItemKeys.PROVIDER_PATIENT_FILLED_ASSESSMENT,
      userType: UserRole.provider,
      reason: response
    });

    dismiss({
      key: ActionItemKeys.PROVIDER_PATIENT_FILLED_ASSESSMENT,
      response,
      ...data
    });
  }, [ dismiss ]);

  const handleCloseRefer = useCallback((note?: string) => {
    if (note) {
      handleDismmiss({
        id: patient.patientId,
        providerId: me.id,
        providerNote: note
      }, NotificationResponse.email);
    }

    setModalOpen(null);
  }, [ handleDismmiss, me.id, patient.patientId ]);

  const handleCloseDefer = useCallback((reason?: string) => {
    if (reason) {
      trackConfirmedToDoDismissal({
        toDoType: ActionItemKeys.PROVIDER_PATIENT_FILLED_ASSESSMENT,
        userType: UserRole.provider,
        reason
      });

      handleDismmiss({
        id: patient.patientId,
      });
    }

    setModalOpen(null);
  }, [ handleDismmiss, patient.patientId ]);

  return (
    <>
      <StackableCard className={classes.background}>
        <Box className={classes.content}>
          <Typography variant="h6">
            {l10n.dashboard.actionItems.patientFilledAssessment.highRiskTitle(patient.displayName.defaultDisplay)}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {l10n.dashboard.actionItems.patientFilledAssessment.highRiskSubtitle(date, time)}
          </Typography>
          <Box mt={1}>
            <Typography>
              {l10n.dashboard.actionItems.patientFilledAssessment.highRiskBody}
            </Typography>
          </Box>
          <Box mt={1}>
            <List>
              {formData.map((x, idx) => (
                <ListItem key={`form-${idx}`} divider classes={{ divider: classes.divider }}>
                  <ListItemText primary={<LineItem {...x} />} />
                </ListItem>
              ))}
            </List>
          </Box>
          <Box className={classes.actions}>
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              onClick={handleRefer}
            >
              {l10n.dashboard.actionItems.patientFilledAssessment.refer}
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleDefer}
            >
              {l10n.dashboard.actionItems.patientFilledAssessment.notNow}
            </Button>
          </Box>
        </Box>
      </StackableCard>
      <PsychReferralModal
        patientName={patient.displayName.defaultDisplay}
        open={modalOpen === 'refer'}
        onClose={handleCloseRefer}
      />
      <NoReferModal
        patientName={patient.displayName.defaultDisplay}
        open={modalOpen === 'defer'}
        onClose={handleCloseDefer}
      />
    </>
  );
};

const useStyles = makeStyles({ name: 'HighRiskAssessmentActionItem' })(theme => ({
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gridGap: theme.spacing(1),
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
    }
  },
  background: {
    backgroundColor: theme.palette.info.background,
  },
  content: {
    backgroundColor: theme.palette.common.white,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3)
  },
  divider: {
    '&:last-child': {
      borderBottom: 'none',
    }
  }
}));
